import React from "react";
import { z } from "zod";

// components
import { FormCheckboxGroup, FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../../../../components/Inputs/SelectEmployee";
import { Option } from "../../../../../../../components/Inputs/SelectionOptions/Checkboxes";

export const StatusSelectionOptions: Option[] = [
	{
		label: "Active",
		value: "active",
	},
	{
		label: "Completed",
		value: "completed",
	},
	{
		label: "Scheduled",
		value: "scheduled",
	},
];

export const Reviews360Options: Option[] = [
	{
		label: "360 Enabled",
		value: "easyAddEnabled",
	},
	{
		label: "Awaiting Approval",
		value: "awaitingApproval",
	},
];

const AllReviewsFiltersSchema = z.object({
	status: z.array(z.string()).optional(),
	employee: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.nullable()
		.optional(),
	reviews360: z.string().optional(),
});

export type AllReviewsFilters = z.infer<typeof AllReviewsFiltersSchema>;

interface AllReviewsFiltersProps {
	isOpen: boolean;
	onClose: () => void;
	filters: AllReviewsFilters;
	onApplyFilters: (filters: AllReviewsFilters) => void;
}

const AllReviewsFilters = ({ isOpen, onClose, filters, onApplyFilters }: AllReviewsFiltersProps) => {
	const defaultValues: AllReviewsFilters = {
		status: filters?.status ?? [],
		employee: filters?.employee,
		reviews360: filters?.reviews360,
	};

	const handleApplyFilters = ({
		value,
	}: {
		value: Partial<AllReviewsFilters>;
		resetForm: () => void;
		setError: (field: keyof AllReviewsFilters, error: string) => void;
	}) => {
		const formattedState = { ...value };
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key as keyof AllReviewsFilters];
			if (
				filterValue === null ||
				filterValue === undefined ||
				(Array.isArray(filterValue) && filterValue.length === 0)
			) {
				delete formattedState[key as keyof AllReviewsFilters];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={AllReviewsFiltersSchema}
			defaultValues={defaultValues}
			onSubmit={handleApplyFilters}
		>
			<div className="space-y-4">
				<FormCheckboxGroup id="status" label="Status" options={StatusSelectionOptions} />
				<FormField<{ id: string; name: string } | null>
					id="employee"
					label="Employee"
					input={(field) => (
						<SingleSelectEmployee
							placeholder="Make a selection"
							value={field.value}
							onChange={(employeeId, employeeName) => {
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								);
							}}
						/>
					)}
				/>
				<FormRadioButtons id="reviews360" label="360 Reviews" options={Reviews360Options} />
			</div>
		</PanelFilters>
	);
};

export default AllReviewsFilters;
